import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSnackbar } from 'notistack';
import { configureWorkyAmplify } from './WorkyAmplifyConfig';
import { confirmMfaSignInChallengeResponse } from './WorkyCognitoAuthService';
import { CognitoSignInOutcomeType } from './WorkyCognitoAuthServiceInterfaces';
import { handleCognitoPasswordSignInOutcome } from './WorkyCustomAuthSignInOutcomeService';
import {
    clearPendingMfaChallengeState,
    readPendingMfaChallengeState,
    storePendingMfaChallengeState,
} from './WorkyPendingMfaChallengeService';
import type { PendingMfaChallengeState } from './WorkyPendingMfaChallengeInterfaces';
import { CognitoMfaChallengeStep } from './WorkyPendingMfaChallengeInterfaces';
import { WORKY_WEB_APP_PATHS } from '../WorkyWebAppPaths';
import { WORKY_BRAND_LOGO_SRC } from '../common/WorkyBrandConstants';
import {
    WORKY_SUPPORT_EMAIL,
    WORKY_SUPPORT_PHONE_DIGITS,
    WORKY_SUPPORT_PHONE_DISPLAY,
} from '../common/WorkySupportContactConstants';
import './WorkyLoginPage.css';

const MFA_METHOD_SMS_RESPONSE: string = 'SMS';
const MFA_METHOD_TOTP_RESPONSE: string = 'TOTP';

const resolveMfaChallengeTitle = (mfaChallengeStep: CognitoMfaChallengeStep): string => {
    if (mfaChallengeStep === CognitoMfaChallengeStep.CONTINUE_SIGN_IN_WITH_MFA_SELECTION) {
        return 'Choose verification method';
    }
    if (mfaChallengeStep === CognitoMfaChallengeStep.CONFIRM_SIGN_IN_WITH_TOTP_CODE) {
        return 'Authenticator verification';
    }
    return 'Verification code';
};

const resolveMfaChallengeSubtitle = (
    emailAddress: string,
    mfaChallengeStep: CognitoMfaChallengeStep
): string => {
    if (mfaChallengeStep === CognitoMfaChallengeStep.CONTINUE_SIGN_IN_WITH_MFA_SELECTION) {
        return `Select how you want to verify ${emailAddress}.`;
    }
    if (mfaChallengeStep === CognitoMfaChallengeStep.CONFIRM_SIGN_IN_WITH_TOTP_CODE) {
        return `Enter the code from your authenticator app for ${emailAddress}.`;
    }
    return `Enter the verification code sent for ${emailAddress}.`;
};

const WorkyMfaChallengePage = (): JSX.Element => {
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();

    const [pendingMfaChallengeState, setPendingMfaChallengeState] =
        useState<PendingMfaChallengeState | null>(null);
    const [verificationCode, setVerificationCode] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    useEffect(() => {
        configureWorkyAmplify();
        const storedPendingMfaChallengeState: PendingMfaChallengeState | null =
            readPendingMfaChallengeState();
        if (storedPendingMfaChallengeState === null) {
            history.replace(WORKY_WEB_APP_PATHS.login);
            return;
        }
        setPendingMfaChallengeState(storedPendingMfaChallengeState);
    }, [history]);

    const handleMfaSignInOutcome = useCallback(
        async (signInOutcome: Awaited<ReturnType<typeof confirmMfaSignInChallengeResponse>>): Promise<void> => {
            if (pendingMfaChallengeState === null) {
                return;
            }

            if (signInOutcome.outcomeType === CognitoSignInOutcomeType.MFA_REQUIRED) {
                const updatedPendingMfaChallengeState: PendingMfaChallengeState = {
                    emailAddress: signInOutcome.emailAddress,
                    mfaChallengeStep: signInOutcome.mfaChallengeStep,
                    primaryWorkyCorpId: pendingMfaChallengeState.primaryWorkyCorpId,
                };
                storePendingMfaChallengeState(updatedPendingMfaChallengeState);
                setPendingMfaChallengeState(updatedPendingMfaChallengeState);
                setVerificationCode('');
                enqueueSnackbar('Continue verification with the selected method.', { variant: 'info' });
                return;
            }

            const didCompleteSignIn: boolean = await handleCognitoPasswordSignInOutcome({
                signInOutcome,
                history,
                enqueueSnackbar,
                primaryWorkyCorpId: pendingMfaChallengeState.primaryWorkyCorpId,
                successMessage: 'Signed in successfully.',
            });

            if (didCompleteSignIn) {
                return;
            }
        },
        [enqueueSnackbar, history, pendingMfaChallengeState]
    );

    const handleSubmitVerificationCode = useCallback(
        async (submitEvent: React.FormEvent<HTMLFormElement>): Promise<void> => {
            submitEvent.preventDefault();

            if (isSubmitting || pendingMfaChallengeState === null) {
                return;
            }

            setIsSubmitting(true);
            try {
                const signInOutcome = await confirmMfaSignInChallengeResponse({
                    emailAddress: pendingMfaChallengeState.emailAddress,
                    challengeResponse: verificationCode,
                });
                await handleMfaSignInOutcome(signInOutcome);
            } finally {
                setIsSubmitting(false);
            }
        },
        [handleMfaSignInOutcome, isSubmitting, pendingMfaChallengeState, verificationCode]
    );

    const handleSelectMfaMethod = useCallback(
        async (mfaMethodResponse: string): Promise<void> => {
            if (isSubmitting || pendingMfaChallengeState === null) {
                return;
            }

            setIsSubmitting(true);
            try {
                const signInOutcome = await confirmMfaSignInChallengeResponse({
                    emailAddress: pendingMfaChallengeState.emailAddress,
                    challengeResponse: mfaMethodResponse,
                });
                await handleMfaSignInOutcome(signInOutcome);
            } finally {
                setIsSubmitting(false);
            }
        },
        [handleMfaSignInOutcome, isSubmitting, pendingMfaChallengeState]
    );

    const handleBackToLoginClick = useCallback((): void => {
        clearPendingMfaChallengeState();
        history.push(WORKY_WEB_APP_PATHS.login);
    }, [history]);

    const handleNavigateHomeClick = useCallback((): void => {
        history.push(WORKY_WEB_APP_PATHS.home);
    }, [history]);

    if (pendingMfaChallengeState === null) {
        return (
            <div className="worky-login-page">
                <div className="worky-login-card worky-login-card-loading">
                    <CircularProgress size={32} />
                    <p className="worky-login-subtitle">Loading verification…</p>
                </div>
            </div>
        );
    }

    const mfaChallengeTitle: string = resolveMfaChallengeTitle(pendingMfaChallengeState.mfaChallengeStep);
    const mfaChallengeSubtitle: string = resolveMfaChallengeSubtitle(
        pendingMfaChallengeState.emailAddress,
        pendingMfaChallengeState.mfaChallengeStep
    );
    const isMfaMethodSelectionStep: boolean =
        pendingMfaChallengeState.mfaChallengeStep ===
        CognitoMfaChallengeStep.CONTINUE_SIGN_IN_WITH_MFA_SELECTION;

    return (
        <div className="worky-login-page">
            <button
                type="button"
                className="worky-login-back-home-button"
                onClick={handleBackToLoginClick}
            >
                <ArrowBackIcon fontSize="small" />
                Back to sign in
            </button>
            <div className="worky-login-card">
                <button
                    type="button"
                    className="worky-login-logo-button"
                    onClick={handleNavigateHomeClick}
                    aria-label="Worky home"
                >
                    <img src={WORKY_BRAND_LOGO_SRC} alt="Worky" className="worky-login-logo-image" />
                </button>

                <h1 className="worky-login-title">{mfaChallengeTitle}</h1>
                <p className="worky-login-subtitle">{mfaChallengeSubtitle}</p>

                {isMfaMethodSelectionStep ? (
                    <div className="worky-login-form">
                        <Button
                            type="button"
                            variant="contained"
                            color="primary"
                            className="worky-login-submit-button"
                            disabled={isSubmitting}
                            fullWidth
                            onClick={(): void => {
                                void handleSelectMfaMethod(MFA_METHOD_SMS_RESPONSE);
                            }}
                        >
                            {isSubmitting ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                'Text message (SMS)'
                            )}
                        </Button>
                        <Button
                            type="button"
                            variant="outlined"
                            color="primary"
                            disabled={isSubmitting}
                            fullWidth
                            onClick={(): void => {
                                void handleSelectMfaMethod(MFA_METHOD_TOTP_RESPONSE);
                            }}
                        >
                            Authenticator app
                        </Button>
                    </div>
                ) : (
                    <form className="worky-login-form" onSubmit={handleSubmitVerificationCode}>
                        <TextField
                            label="Verification code"
                            value={verificationCode}
                            onChange={(changeEvent): void => setVerificationCode(changeEvent.target.value)}
                            required
                            fullWidth
                            autoComplete="one-time-code"
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            className="worky-login-submit-button"
                            disabled={isSubmitting}
                            fullWidth
                        >
                            {isSubmitting ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                'Verify and sign in'
                            )}
                        </Button>
                    </form>
                )}

                <div className="worky-login-support-footer">
                    <span className="worky-login-support-label">Need help? Contact Us</span>{' '}
                    <a className="worky-login-support-link" href={`mailto:${WORKY_SUPPORT_EMAIL}`}>
                        {WORKY_SUPPORT_EMAIL}
                    </a>
                    {' · '}
                    <a className="worky-login-support-link" href={`tel:${WORKY_SUPPORT_PHONE_DIGITS}`}>
                        {WORKY_SUPPORT_PHONE_DISPLAY}
                    </a>
                </div>
            </div>
        </div>
    );
};

export { WorkyMfaChallengePage };
